import React, { useEffect, useMemo, useState } from 'react';
import Accordion from './components/Accordion';
import VehicleDetailsSection from './VehicleDetailsSection';
import VehicleConditionReportSection from './VehicleConditionReportSection';
import VehicleWindowStickerSection from './VehicleWindowStickerSection';
import VehicleCertificateSection from './VehicleCertificateSection';
import SalesPacketEmptyState from './SalesPacketEmptyState';
import MobileNav from './components/mobileNav';
import PrintModal from './components/PrintModal';
import SalesLeadDialog from './components/SalesLeadDialog';
import { CarketaDisclosure } from './components/CarketaDisclosure';
import { CheckAvailabilityDialog } from 'src/@carketa/vehicle_dialog/components/views/VehicleMainView/dialogs/CheckAvailabilityDialog';
import Transportation from './components/Transportation';
import CarketaConnect from './components/CarketaConnect';
import { trpc } from 'src/utils/trpc';
import { useAnalytics } from 'use-analytics';
import { useRouterParams } from 'src/hooks/useRouterParams';
import { OrganizationPermission } from 'src/@carketa/components/control/OrganizationPermission';
import { FEATURE_FLAG } from 'src/server/common/permissions';
import { OrganizationSettings } from 'src/types/organizations/organizations';
import {
  defaultSalesPacketMenu,
  isSalesPacketMenuItemAvailable,
  isSalesPacketMenuItemVisible,
} from '../organization/settings/SettingsTable';
import { normalizeExternalUrl } from 'src/utils/externalWebsiteUrl';
import { CheckCircle2, Mail, Phone, Pin, Printer, Share, X } from 'lucide-react';
import { Box } from 'src/ui/box';
import { Alert, AlertDescription } from 'src/ui/alert';
import { Button } from 'src/ui/button';


type CarketaValuationProps = {
  vin: string;
};

function CarketaValuation({ vin }: CarketaValuationProps) {
  const { page, track } = useAnalytics();
  const { get, set, setMany } = useRouterParams();

  const inspectionId = String(get('inspectionId') || '');
  const pdfMode = Boolean(get('pdfMode') || false);

  const { data: vehicle } = trpc.vehicles.getByStickerOrId.useQuery(
    {
      vin: vin ?? '',
    },
    {
      placeholderData: (previousData) => previousData,
      refetchOnWindowFocus: false,
    }
  );

  const { data: neovin } = trpc.window_sticker.getNeoVin.useQuery(
    {
      vin: vehicle ? vehicle.vin : '',
    },
    {
      placeholderData: (previousData: any) => previousData,
      refetchOnWindowFocus: false,
    }
  );

  const { data: vehicle_inspections, isLoading: inspectionsLoading } =
    trpc.vehicle_inspection.getAllConditionReportByVehicleId.useQuery(
      { vehicle_id: vehicle ? vehicle.id : '' },
      {
        refetchOnWindowFocus: false,
        placeholderData: (previousData) => previousData,
      }
    );

  const organization_settings = vehicle?.organization
    ?.settings as OrganizationSettings;
  const locationWebsiteUrl = normalizeExternalUrl(vehicle?.location?.website);

  const orgFeatureFlags = vehicle?.organization?.feature_flags;

  // Tabs the dealer has hidden are dropped everywhere: the side menu, the
  // default landing section, and the ?section= query param. Without the last
  // one a hidden section stays reachable by direct link.
  const visibleMenus = useMemo(
    () =>
      // A persisted empty array is truthy, so check length — otherwise an
      // empty menu skips the defaults and forces the fallback.
      (organization_settings?.sales_packet_menu?.length
        ? organization_settings.sales_packet_menu
        : defaultSalesPacketMenu
      )
        .filter(isSalesPacketMenuItemVisible)
        // A tab can be visible in settings and still render nothing: the
        // inspection tabs need a published inspection on THIS vehicle, and
        // Carketa Connect needs the org flag. Both are invisible to the
        // settings page, so re-check them here.
        .filter((menu: any) => {
          if (
            !isSalesPacketMenuItemAvailable(
              menu,
              (orgFeatureFlags ?? []) as string[]
            )
          ) {
            return false;
          }
          if (
            menu?.section === 'vehicle-reports' ||
            menu?.section === 'checklist-certificate'
          ) {
            // Only offer these once data confirms there is something to open —
            // a tab that leads to a blank report is worse than no tab.
            return Boolean(vehicle_inspections?.length);
          }
          return true;
        }),
    [organization_settings?.sales_packet_menu, vehicle_inspections, orgFeatureFlags]
  );

  // No fallback tab: showing Vehicle Details here would expose a tab the
  // dealer deliberately hid. Render a neutral empty state instead — but not
  // while the inspections are still in flight, or an inspection-only packet
  // flashes "no information" before its tab appears.
  const showEmptyState = !visibleMenus.length && !inspectionsLoading;

  const requestedSection = String(get('section') || '');
  const section =
    requestedSection &&
    visibleMenus.some((menu: any) => menu.section === requestedSection)
      ? requestedSection
      : // Empty when nothing is visible, so no panel matches and the empty
        // state is the only thing rendered.
        visibleMenus[0]?.section || '';

  const [showCopied, setShowCopied] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [salesLeadOpen, setSalesLeadOpen] = useState(false);

  const [checkAvailabilityModalOpen, setcheckAvailabilityModalOpen] =
    useState(false);

  const handleMouseOver = (sectionOver: string) => {
    if (section === sectionOver) return;
    set('section', sectionOver);
    track(
      'section-over',
      {
        section: sectionOver,
      },
      {
        component: 'sales-packet',
        custom: { vin },
      }
    );
  };
  const handleCloseSnackBar = () => {
    setShowCopied(false);
  };

  const openNewTabWithSelectedSection = (tabOptions: {
    inspectionId: string | null;
    targetElementId: string;
  }) => {
    const { inspectionId, targetElementId } = tabOptions;

    setMany([
      { name: 'inspectionId', value: inspectionId || undefined },
      { name: 'section', value: targetElementId || undefined },
    ]);

    track(
      'section-click',
      {
        section: targetElementId,
        inspectionId: inspectionId,
      },
      {
        component: 'sales-packet',
        custom: { vin },
      }
    );

    const targetElement = document.getElementById(targetElementId);
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetTop - 80,
        behavior: 'smooth',
      });
    }
  };

  const sidebarMenu = (menuOptions: {
    menu: string;
    title: string;
    subTitle: string;
    id: string | null;
  }) => {
    const { menu = '', title, subTitle, id } = menuOptions;

    return (
      <div
        key={`${id}-${title}`}
        className={`border p-4 rounded mt-2 cursor-pointer ${
          inspectionId
            ? inspectionId === id
              ? 'bg-green text-white font-medium'
              : ''
            : menu === section
            ? 'bg-green text-white font-medium'
            : ''
        }`}
        onClick={() =>
          openNewTabWithSelectedSection({
            inspectionId: id,
            targetElementId: menu,
          })
        }
      >
        <div className="flex justify-start">
          <div>
            <p className="text-sm">{title}</p>
            <p className={`text-xs gray-500`}>{subTitle}</p>
          </div>
        </div>
      </div>
    );
  };

  const handleCloseCheckAvailabilityDialog = () => {
    setcheckAvailabilityModalOpen(false);
  };

  useEffect(() => {
    page({}, { component: 'sales-packet', custom: { vin } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCopied) return;
    const timeout = window.setTimeout(() => setShowCopied(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [showCopied]);

  if (!vehicle) {
    return <></>;
  }

  return (
    <Box className="h-full w-full bg-white text-black">
      <Box>
        <div className={section === 'vehicle-reports' && pdfMode ? 'flex flex-col ' : 'flex flex-col '}>
          <nav className="flex justify-between h-10 items-center flex-no-wrap fixed top-0 w-full z-10 bg-[#393939]">
            <Box
              className={
                pdfMode ? 'hidden' : 'flex justify-center items-center'
              }
            >
              <MobileNav>
                <Box className="flex justify-center align-center flex-col">
                  {visibleMenus.map((data: any) => {
                    if (data?.text?.toUpperCase() === 'VEHICLE DETAILS') {
                      return sidebarMenu({
                        menu: 'vehicle-details',
                        title: 'Vehicle Details',
                        subTitle: 'View vehicle information',
                        id: null,
                      });
                    }
                    if (data?.text?.toUpperCase() === 'CARKETA INSPECTION') {
                      return vehicle_inspections
                        ? vehicle_inspections.map((vehicle_inspection: any) => {
                            return sidebarMenu({
                              menu: 'vehicle-reports',
                              title: vehicle_inspection.inspection_template.title,
                              subTitle: 'View vehicle inspection',
                              id: vehicle_inspection.id,
                            });
                          })
                        : null;
                    }

                    if (data?.text?.toUpperCase() === 'CARKETA WINDOW STICKER') {
                      return sidebarMenu({
                        menu: 'window-sticker',
                        title: 'Carketa Window Sticker',
                        subTitle: 'View carketa sticker',
                        id: null,
                      });
                    }

                    if (data?.text?.toUpperCase() === 'CHECKLIST CERTIFICATE') {
                      return vehicle_inspections && vehicle_inspections.length
                        ? sidebarMenu({
                            menu: 'checklist-certificate',
                            title: 'Checklist Certificate',
                            subTitle: 'View vehicle certificate',
                            id: null,
                          })
                        : null;
                    }

                    if (data?.text?.toUpperCase() === 'TRANSPORTATION') {
                      return sidebarMenu({
                        menu: 'transportation',
                        title: 'Transportation',
                        subTitle: 'View transportation status',
                        id: null,
                      });
                    }

                    if (data?.text?.toUpperCase() === 'CARKETA CONNECT') {
                      return (
                        <OrganizationPermission
                          key={'carketa_connect-mobile'}
                          vehicle_id={vehicle.id}
                          feature_flag={FEATURE_FLAG.carketa_connect}
                        >
                          {sidebarMenu({
                            menu: 'carketa-connect',
                            title: 'Carketa Connect',
                            subTitle: 'View vehicle process',
                            id: null,
                          })}
                        </OrganizationPermission>
                      );
                    }
                  })}
                </Box>
              </MobileNav>
              <img
                className=" py-2 h-10 ml-5"
                src={`https://storage.googleapis.com/carketa-next-assets/next/img/carketa-logo-white.png`}
                alt=""
              />
            </Box>
          </nav>
          <Box>
            <div className="flex flex-col items-center pb-14 pt-10">
              <div className="flex flex-1 overflow-hidden p-4 m4 gap-6 w-full justify-center ">
                <div className={pdfMode ? 'hidden' : 'flex'}>
                  <Box className="w-[300px] hidden md:block">
                    <div className="flex flex-col justify-center items-center gap-6">
                      <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded">
                        <Accordion
                          title="Vehicle Documentation"
                          content={
                            <Box className="flex justify-center align-center flex-col">
                              {visibleMenus.map((data: any) => {
                                if (data?.text?.toUpperCase() === 'VEHICLE DETAILS') {
                                  return sidebarMenu({
                                    menu: 'vehicle-details',
                                    title: 'Vehicle Details',
                                    subTitle: 'View vehicle information',
                                    id: null,
                                  });
                                }
                                if (data?.text?.toUpperCase() === 'CARKETA INSPECTION') {
                                  return vehicle_inspections
                                    ? vehicle_inspections.map((vehicle_inspection: any) => {
                                        return sidebarMenu({
                                          menu: 'vehicle-reports',
                                          title: vehicle_inspection.inspection_template.title,
                                          subTitle: 'View vehicle inspection',
                                          id: vehicle_inspection.id,
                                        });
                                      })
                                    : null;
                                }

                                if (data?.text?.toUpperCase() === 'CARKETA WINDOW STICKER') {
                                  return sidebarMenu({
                                    menu: 'window-sticker',
                                    title: 'Carketa Window Sticker',
                                    subTitle: 'View carketa sticker',
                                    id: null,
                                  });
                                }

                                if (data?.text?.toUpperCase() === 'CHECKLIST CERTIFICATE') {
                                  return vehicle_inspections && vehicle_inspections.length
                                    ? sidebarMenu({
                                        menu: 'checklist-certificate',
                                        title: 'Checklist Certificate',
                                        subTitle: 'View vehicle certificate',
                                        id: null,
                                      })
                                    : null;
                                }

                                if (data?.text?.toUpperCase() === 'TRANSPORTATION') {
                                  return sidebarMenu({
                                    menu: 'transportation',
                                    title: 'Transportation',
                                    subTitle: 'View transportation status',
                                    id: null,
                                  });
                                }

                                if (data?.text?.toUpperCase() === 'CARKETA CONNECT') {
                                  return (
                                    <OrganizationPermission
                                      key={'carketa_connect-full'}
                                      vehicle_id={vehicle.id}
                                      feature_flag={FEATURE_FLAG.carketa_connect}
                                    >
                                      {sidebarMenu({
                                        menu: 'carketa-connect',
                                        title: 'Carketa Connect',
                                        subTitle: 'View vehicle process',
                                        id: null,
                                      })}
                                    </OrganizationPermission>
                                  );
                                }
                              })}
                            </Box>
                          }
                        />
                      </div>
                      <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded">
                        <Accordion
                          title="Dealership Information"
                          content={
                            <div className="p-3">
                              <p className="gray-500 text-sm mb-2">{vehicle?.location?.name} </p>
                              <div className="flex items-center">
                                <Pin className="h-5 w-5 mr-2" />
                                <p className="gray-400 text-sm">
                                  {vehicle?.location?.street1} {vehicle?.location?.city},{' '}
                                </p>
                              </div>
                              <div className="flex items-center m-1">
                                <p className="gray-400 text-sm">
                                  {vehicle?.location?.state} {vehicle?.location?.zip}
                                </p>
                              </div>
                              <div className="flex m-1 items-center">
                                <Mail className="h-5 w-5 mr-2" style={{ display: vehicle?.location?.contact_email ? '' : 'none' }} />
                                <p className="gray-400 text-sm mb-1">{vehicle?.location?.contact_email}</p>
                              </div>
                              <div className="flex m-1 items-center">
                                <Phone className="h-5 w-5 mr-2" style={{ display: vehicle?.location?.contact_phone ? '' : 'none' }} />
                                <p className="gray-400 text-sm mb-1">{vehicle?.location?.contact_phone}</p>
                              </div>
                              {locationWebsiteUrl && (
                                <div className="flex m-1 items-center">
                                  <a href={locationWebsiteUrl}>
                                    <p className="gray-400 text-sm mb-1">{locationWebsiteUrl}</p>
                                  </a>
                                </div>
                              )}
                            </div>
                          }
                        />
                      </div>
                    </div>
                  </Box>
                </div>
                <main className="w-full xl:max-w-[1000px] lg:max-w-[600px] md:max-w-[500px] sm:max-w-[400px] xs:max-w-[400px] ">
                  {showEmptyState && <SalesPacketEmptyState />}
                  {/* No `|| !section` fallthrough — an empty section means
                      nothing is visible, and defaulting to Vehicle Details
                      there would show a tab the dealer hid. */}
                  {section === 'vehicle-details' && (
                    <div onMouseOver={() => handleMouseOver('vehicle-details')} id="vehicle-details">
                      <VehicleDetailsSection vehicle={vehicle} setSalesLeadOpen={setSalesLeadOpen} />
                    </div>
                  )}
                  {section === 'vehicle-reports' && (
                    <div onMouseOver={() => handleMouseOver('vehicle-reports')} id="vehicle-reports">
                      <VehicleConditionReportSection
                        vehicle={vehicle}
                        vehicle_inspections={vehicle_inspections}
                        inspectionId={inspectionId}
                      />
                    </div>
                  )}
                  {section === 'window-sticker' && (
                    <div onMouseOver={() => handleMouseOver('window-sticker')} id="window-sticker">
                      <VehicleWindowStickerSection vehicle={vehicle} neovin={neovin} />
                    </div>
                  )}
                  {section === 'checklist-certificate' && (
                    <div onMouseOver={() => handleMouseOver('checklist-certificate')} id="checklist-certificate">
                      <VehicleCertificateSection vehicle={vehicle} vehicle_inspections={vehicle_inspections} />
                    </div>
                  )}
                  {section === 'transportation' && (
                    <div onMouseOver={() => handleMouseOver('transportation')} id="transportation" className="mb-4">
                      <Transportation vin={vehicle?.vin ?? ''} />
                    </div>
                  )}
                  {section === 'carketa-connect' && (
                    <div onMouseOver={() => handleMouseOver('carketa-connect')} id="carketa-connect" className="mb-4">
                      <CarketaConnect vehicle={vehicle} />
                    </div>
                  )}
                  <div className={pdfMode ? 'hidden' : ''}>
                    <CarketaDisclosure />
                  </div>
                </main>
              </div>
            </div>
          </Box>
          <div
            className={`flex justify-center py-2 fixed bottom-0 w-full z-10 bg-[#393939] ${
              pdfMode ? 'hidden' : ''
            }`}
          >
            <div
              className="w-1/5 flex flex-col items-center cursor-pointer"
              onClick={() => {
                track(
                  'share-click',
                  {
                    location: window.location.toString(),
                  },
                  {
                    component: 'sales-packet',
                    custom: { vin },
                  }
                );
                setShowCopied(true);
                navigator.clipboard.writeText(window.location.toString());
              }}
            >
              <Share className="text-white" />
              <p className="text-green text-xs mt-1">Share</p>
            </div>
            <OrganizationPermission vehicle_id={vehicle.id} feature_flag={FEATURE_FLAG.carketa_connect}>
              <Box
                className="flex flex-col items-center cursor-pointer"
                onClick={() => {
                  track(
                    'check-availability-click',
                    {},
                    {
                      component: 'sales-packet',
                      custom: { vin },
                    }
                  );
                  setcheckAvailabilityModalOpen(true);
                }}
              >
                <CheckCircle2 className="text-white" />
                <p className="text-green text-xs mt-1">Check Availability</p>
              </Box>
            </OrganizationPermission>

            <Box
              className="w-1/5 flex flex-col items-center cursor-pointer"
              onClick={() => {
                track(
                  'print-click',
                  {},
                  {
                    component: 'sales-packet',
                    custom: { vin },
                  }
                );
                setPrintModalOpen(true);
              }}
            >
              <Printer className="text-white" />
              <p className="text-green text-xs mt-1">Print</p>
            </Box>

            {showCopied && (
              <div className="fixed left-1/2 top-4 z-[1400] -translate-x-1/2">
                <Alert
                  variant="contained"
                  severity="success"
                  elevation={3}
                  className="flex items-center gap-2 pr-2"
                >
                  <AlertDescription>Copied to clipboard</AlertDescription>
                  <Button
                    type="button"
                    variant="ghost"
                    size="smIcon"
                    aria-label="close"
                    onClick={handleCloseSnackBar}
                  >
                    <X />
                  </Button>
                </Alert>
              </div>
            )}
          </div>
        </div>
        <PrintModal
          open={printModalOpen}
          vehicle={vehicle}
          inspections={vehicle_inspections}
          handleClose={() => {
            track(
              'print-modal-close',
              {},
              {
                component: 'sales-packet',
                custom: { vin },
              }
            );
            setPrintModalOpen(false);
          }}
        />
        <SalesLeadDialog open={salesLeadOpen} vehicle={vehicle} handleClose={() => setSalesLeadOpen(false)} />
        <CheckAvailabilityDialog
          open={checkAvailabilityModalOpen}
          handleClose={handleCloseCheckAvailabilityDialog}
          vehicle={vehicle}
        />
      </Box>
    </Box>
  );
}

export default CarketaValuation;
